import { type MouseEvent, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import type { SelectedFile, UndecoratedUser } from '@witmetrics/utils';
import { isBlank } from '@witmetrics/utils/strings';
import {
  asyncMap,
  filterOutKeyValue,
  findByKey,
} from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { useToggle } from '@/hooks/useToggle';
import { buildSelectedFile } from '@/utils/files';
import { showErrorToaster } from '@/utils/toasters';
import { useArray } from '@/hooks/useArray';
import { useString } from '@/hooks/useString';
import { useStringId } from '@/hooks/useId';
import Button from '@/components/Button';
import IconButton from '@/components/IconButton';
import Card from '@/components/Card';
import { AddIcon } from '@/icons';
import MentionTextField from '@/components/inputs/MentionTextField';
import AttachmentMenu from '@/menus/AttachmentMenu';
import AttachmentPreviewDialog from '@/dialogs/AttachmentPreviewDialog';
import AttachmentsList, { Attachment } from '@/components/AttachmentsList';
import DeleteAttachmentDialog from '@/dialogs/DeleteAttachmentDialog';
import { classes } from './utils';

export type NotesFieldProps = {
  isSaving: boolean;
  allowAttachments?: boolean;
  className?: string;
  placeholder?: string;
  initialMessage?: string;
  users: UndecoratedUser[];
  onSave: (message: string, attachments: SelectedFile[]) => void;
  onCancel: () => void;
};

export default function NotesField({
  isSaving,
  allowAttachments,
  className,
  placeholder = 'New note...',
  initialMessage = '',
  users,
  onSave,
  onCancel,
}: NotesFieldProps) {
  const menuToggleRef = useRef(null);
  const [message, setMessage] = useString(initialMessage);
  const [attachments, setAttachments, { addItems: addAttachments }] =
    useArray<SelectedFile>();
  const [attachmentId, setAttachmentId, { clear: clearAttachmentId }] =
    useStringId();
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isPreviewDialogOpen, togglePreviewDialog] = useToggle();
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle();

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onSave(message, attachments);
  };

  const handleAddFiles = async (selectedFiles: File[]) => {
    try {
      addAttachments(await asyncMap(selectedFiles, buildSelectedFile));
      toggleMenu(false);
    } catch (err) {
      toggleMenu(false);
      showErrorToaster('Error adding files');
    }
  };

  const handlePreviewAttachment = (uuid: string) => {
    setAttachmentId(uuid);
    togglePreviewDialog(true);
  };

  const handleStartRemoveAttachment = (uuid: string) => {
    setAttachmentId(uuid);
    toggleDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    togglePreviewDialog(false);
    toggleDeleteDialog(false);
    clearAttachmentId();
  };

  const handleRemoveAttachment = () => {
    setAttachments((a) => filterOutKeyValue(a, 'uuid', attachmentId!));
    handleCloseDialogs();
  };

  const renderAttachmentPreviewDialog = () => {
    if (!isPreviewDialogOpen || !attachmentId) return null;
    const attachment = findByKey(attachments, 'uuid', attachmentId)!;
    return (
      <AttachmentPreviewDialog
        name={attachment.file.name}
        src={attachment.source}
        onClose={handleCloseDialogs}
      />
    );
  };

  const hasAttachments = !isEmpty(attachments);

  return (
    <>
      {isMenuOpen && (
        <AttachmentMenu
          anchorTo={menuToggleRef.current}
          open
          onAddFiles={handleAddFiles}
          onClose={() => toggleMenu(false)}
        />
      )}
      {isDeleteDialogOpen && attachmentId && (
        <DeleteAttachmentDialog
          onDelete={handleRemoveAttachment}
          onClose={handleCloseDialogs}
        />
      )}
      {renderAttachmentPreviewDialog()}
      <Card className={twMerge(classes.card, className)}>
        <MentionTextField
          autoFocus
          disabled={isSaving}
          className={classes.textField}
          placeholder={placeholder}
          value={message}
          users={users}
          onChange={setMessage}
        />
        {hasAttachments && (
          <AttachmentsList>
            {attachments.map(({ uuid, previewSource, file }) => (
              <Attachment
                key={uuid}
                src={previewSource}
                alt={file.name}
                onPreview={() => handlePreviewAttachment(uuid)}
                onDelete={() => handleStartRemoveAttachment(uuid)}
              />
            ))}
          </AttachmentsList>
        )}
        <div
          className={twMerge(
            classes.buttonsWrapper,
            hasAttachments && classes.withAttachments
          )}>
          <Button className={classes.button} variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <div className={classes.saveWrapper}>
            {allowAttachments && (
              <IconButton
                ref={menuToggleRef}
                className={classes.menuButton}
                title="Add attachment"
                onClick={() => toggleMenu()}>
                <AddIcon />
              </IconButton>
            )}
            <Button
              disabled={isSaving || isBlank(message)}
              className={classes.button}
              onClick={handleSave}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
}
